import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Form, Input, Radio, Select, Button, Checkbox } from "antd";
import { pick } from "lodash";
import dayjs from "dayjs";

import { apiBase } from "../utils/apiBase";
import SearchSelect from "../views/common/SearchSelect";
import UserSelect from "../views/common/UserSelect";
import {
  activityTypeOptions,
  activityTypeIcons,
  activityPriorityOptions,
} from "../common/activities";
import {
  recordTypeOptions,
  recordSearchConfigFor,
} from "../common/recordTypes";
import { fetchUndoneActivityCount } from "../actions/activities";
import DateTimePicker from "../views/common/DateTimePicker";

import styles from "./ActivityForm.module.scss";

const activityDefaultDueAt = () => dayjs().startOf("day");

const editFormFields = [
  "title",
  "description",
  "type",
  "priority",
  "due_at",
  "user_id",
  "done",
];

const typeOptionsWithIcons = activityTypeOptions.map((option) => ({
  ...option,
  label: (
    <span className={styles.typeOption}>
      <span className={styles.typeOptionIcon}>
        {activityTypeIcons[option.value]}
      </span>
      {option.label}
    </span>
  ),
}));

const ActivityForm = (props) => {
  const {
    activity,
    record,
    onSaved,
    onLoadingChange,
    showRecordFields = false,
    compact = false,
    footer = false,
    form: externalForm,
  } = props;

  const { message } = useSelector((state) => state.antd);
  const dispatch = useDispatch();
  const [innerForm] = Form.useForm();
  const form = externalForm || innerForm;

  const [isLoading, setIsLoading] = useState(false);
  const [recordType, setRecordType] = useState(
    record?.record_type || undefined
  );
  const currentUserId = useSelector((state) => state.user?.data?.id);
  const recordSearch = recordSearchConfigFor(recordType);

  const isEdit = !!activity;

  const setLoading = (value) => {
    setIsLoading(value);
    onLoadingChange?.(value);
  };

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        record_type: record.record_type,
        record_id: record.record_id,
      });
      setRecordType(record.record_type);
    }
  }, [record, form]);

  useEffect(() => {
    if (activity) {
      form.setFieldsValue({
        ...pick(activity, editFormFields),
        due_at: activity.due_at ? dayjs(activity.due_at) : null,
      });
    }
  }, [activity, form]);

  const handleSubmit = (values) => {
    setLoading(true);

    const payload = {
      title: values.title,
      description: values.description || null,
      type: values.type,
      priority: values.priority || null,
      due_at: values.due_at ? values.due_at.toISOString() : null,
      user_id: values.user_id,
    };

    const done = !!values.done;

    const request = isEdit
      ? apiBase.patch(`/activities/${activity.id}`, {
          ...payload,
          done,
        })
      : apiBase.post("/activities", {
          ...payload,
          done,
          record_type: record?.record_type || values.record_type,
          record_id: record?.record_id || values.record_id,
        });

    request
      .then((res) => {
        setLoading(false);

        if (!isEdit) {
          form.resetFields();
          form.setFieldValue("due_at", activityDefaultDueAt());
          setRecordType(record?.record_type || undefined);
        }

        message.success(isEdit ? "Activity updated" : "Activity created");

        dispatch(fetchUndoneActivityCount());
        onSaved?.(res.data);
      })
      .catch((err) => {
        setLoading(false);
        const msg =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data?.errors) ||
          `Failed to ${isEdit ? "update" : "create"} activity`;
        message.error(msg);
      });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size={compact ? "small" : undefined}
      className={compact ? styles.compactForm : undefined}
      onFinish={handleSubmit}
      disabled={isLoading}
      initialValues={{
        type: "task",
        due_at: activityDefaultDueAt(),
        user_id: currentUserId,
        record_type: record?.record_type,
        record_id: record?.record_id,
      }}
    >
      <Form.Item
        label={compact ? undefined : "Title"}
        name="title"
        rules={[{ required: true, message: "Title is required" }]}
      >
        <Input placeholder="Title" />
      </Form.Item>

      <Form.Item
        className={styles.descriptionItem}
        style={{ "--min-rows": compact ? 2 : 3 }}
        label={compact ? undefined : "Description"}
        name="description"
      >
        <Input.TextArea placeholder="Description" />
      </Form.Item>

      <Form.Item
        label={compact ? undefined : "Type"}
        name="type"
        rules={[{ required: true, message: "Type is required" }]}
      >
        <Radio.Group
          options={typeOptionsWithIcons}
          optionType="button"
          className={styles.typeGroup}
        />
      </Form.Item>

      <Row gutter={8} align="middle">
        <Col span={compact ? 5 : 24}>
          <Form.Item label={compact ? undefined : "Priority"} name="priority">
            <Select
              options={activityPriorityOptions}
              allowClear
              placeholder="Priority"
            />
          </Form.Item>
        </Col>
        <Col span={compact ? 10 : 24}>
          <Form.Item
            label={compact ? undefined : "Due at"}
            name="due_at"
            rules={[{ required: true, message: "Due date is required" }]}
          >
            <DateTimePicker />
          </Form.Item>
        </Col>
        <Col span={compact ? 6 : 24}>
          <Form.Item
            label={compact ? undefined : "User"}
            name="user_id"
            rules={[{ required: true, message: "User is required" }]}
          >
            <UserSelect />
          </Form.Item>
        </Col>
        <Col span={compact ? 3 : 24}>
          <Form.Item name="done" valuePropName="checked">
            <Checkbox>Done</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      {showRecordFields && (
        <>
          <Form.Item
            label="Record type"
            name="record_type"
            rules={[{ required: true, message: "Record type is required" }]}
          >
            <Select
              options={recordTypeOptions}
              disabled={!!record}
              onChange={(value) => {
                setRecordType(value);
                form.setFieldValue("record_id", undefined);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Record"
            name="record_id"
            rules={[{ required: true, message: "Record is required" }]}
          >
            {recordSearch ? (
              <SearchSelect
                apiPath={recordSearch.apiPath}
                searchField={recordSearch.searchField}
                renderLabel={recordSearch.renderLabel}
                renderExtra={false}
                disabled={!!record}
                placeholder={`Search ${recordType.replace("_", " ")}`}
              />
            ) : (
              <Select
                disabled
                placeholder={
                  recordType
                    ? "Search not available — set via URL"
                    : "Pick record type first"
                }
              />
            )}
          </Form.Item>
        </>
      )}

      {footer && (
        <div className={styles.footer}>
          <Button
            size={compact ? "small" : undefined}
            type={"default"}
            loading={isLoading}
            onClick={() => form.submit()}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default ActivityForm;
